import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

interface PageHeroProps {
  title: string;
  highlight?: string;
  description?: string;
  badge?: string;
}

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

const item = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
  },
};

export const PageHero = ({
  title,
  highlight,
  description,
  badge,
}: PageHeroProps) => {
  const { language } = useLanguage();

  return (
    <section className="relative overflow-hidden pt-32 lg:pt-44 lg:pb-16 bg-[#070B14] pb-0">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-primary/10 rounded-full blur-[120px] opacity-60 hidden md:block" />
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[300px] h-[200px] bg-primary/15 rounded-full blur-[80px] opacity-40 hidden md:block" />
      </div>

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="container mx-auto px-4 relative z-10 text-center max-w-4xl pb-0"
      >
        {badge && (
          <motion.div
            variants={item}
            className="inline-flex items-center gap-2 mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <span className="text-sm text-slate-400 font-light tracking-wide">
              {badge}
            </span>
          </motion.div>
        )}

        <motion.h1
          variants={item}
          className="text-4xl lg:text-5xl font-light text-white leading-tight mb-6"
          dir={language === "ar" ? "rtl" : "ltr"}
        >
          {title}{" "}
          {highlight && <span className="text-gradient-pink">{highlight}</span>}
        </motion.h1>

        {description && (
          <motion.p
            variants={item}
            className="text-base lg:text-lg text-slate-400 font-light leading-relaxed max-w-2xl mx-auto"
          >
            {description}
          </motion.p>
        )}

        {/* Decorative line */}
        <motion.div
          variants={item}
          className="mt-10 flex items-center justify-center gap-3 mb-10"
        >
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-primary/40" />
          <div className="h-1.5 w-1.5 rounded-full bg-primary/60" />
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-primary/40" />
        </motion.div>
      </motion.div>
    </section>
  );
};
